import React, { useState } from 'react';
import './FeedbackPage.css';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

const CATEGORIES = [
  {
    id: 'bug',
    label: 'Something not working?',
    sub: 'Report bugs, wrong prices, or issues you\'ve spotted',
    icon: '🐛',
    placeholder: 'What went wrong? What did you expect to happen?',
  },
  {
    id: 'idea',
    label: 'Got an idea?',
    sub: 'Tell us what would make this more useful for you',
    icon: '💡',
    placeholder: 'What would make this more useful for you?',
  },
  {
    id: 'general',
    label: 'Share your thoughts',
    sub: 'What\'s working well? What could be better?',
    icon: '💬',
    placeholder: 'What\'s working well? What could be better?',
  },
];

function FeedbackPage({ onBack, originPage = 'more' }) {
  const [step,     setStep]    = useState('pick');  // 'pick' | 'write' | 'done'
  const [category, setCategory] = useState(null);
  const [message,  setMessage]  = useState('');
  const [sending,  setSending]  = useState(false);
  const [error,    setError]    = useState(null);

  const handlePick = (cat) => {
    setCategory(cat);
    setMessage('');
    setError(null);
    setStep('write');
  };

  const handleBack = () => {
    if (step === 'write') { setStep('pick'); return; }
    onBack();
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError(null);
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, 'feedback'), {
        type:       category.id,
        message:    message.trim(),
        userId:     user?.uid ?? null,
        userEmail:  user?.email ?? null,
        context: {
          page:      originPage,
          productId: null,
          store:     null,
        },
        createdAt:  serverTimestamp(),
        status:     'new',
        adminNotes: null,
      });
      setStep('done');
    } catch (err) {
      console.error('[Feedback] submit error:', err);
      setError('Something went wrong — please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fbp-page">

      {/* ── Back header ── */}
      {step !== 'done' && (
        <button className="fbp-back-btn" onClick={handleBack} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="13,4 7,10 13,16"/>
          </svg>
          {step === 'write' ? category.label : 'Back'}
        </button>
      )}

      {/* ── Screen 1: Category picker ── */}
      {step === 'pick' && (
        <div className="fbp-content">
          <div className="fbp-hero">
            <h1 className="fbp-title">Feedback</h1>
            <p className="fbp-delight">We read every message — this app is built with you</p>
          </div>

          <div className="fbp-categories">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className="fbp-cat-row"
                onClick={() => handlePick(cat)}
              >
                <span className="fbp-cat-icon" aria-hidden="true">{cat.icon}</span>
                <span className="fbp-cat-text">
                  <span className="fbp-cat-label">{cat.label}</span>
                  <span className="fbp-cat-sub">{cat.sub}</span>
                </span>
                <svg className="fbp-cat-chevron" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="7,4 13,10 7,16"/>
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Screen 2: Write ── */}
      {step === 'write' && (
        <div className="fbp-content">
          <div className="fbp-hero">
            <span className="fbp-cat-emoji" aria-hidden="true">{category.icon}</span>
            <h1 className="fbp-title">{category.label}</h1>
          </div>

          <textarea
            className="fbp-textarea"
            placeholder={category.placeholder}
            value={message}
            onChange={e => setMessage(e.target.value)}
            autoFocus
          />

          {error && <p className="fbp-error">{error}</p>}

          <button
            className="fbp-submit-btn"
            onClick={handleSubmit}
            disabled={!message.trim() || sending}
          >
            {sending
              ? <span className="fbp-spinner" aria-hidden="true" />
              : 'Send feedback'
            }
          </button>
        </div>
      )}

      {/* ── Screen 3: Done ── */}
      {step === 'done' && (
        <div className="fbp-done">
          <span className="fbp-done-icon" aria-hidden="true">✉️</span>
          <h2 className="fbp-done-title">Thank you</h2>
          <p className="fbp-done-sub">
            We'll take a look — your input helps make ChopMyShop better.
          </p>
          <button className="fbp-submit-btn" onClick={onBack}>Done</button>
        </div>
      )}

    </div>
  );
}

export default FeedbackPage;
